/**
 * Contact Group Service
 * Handles contact group and recipient management
 */

import { apiClient, handleApiError } from './apiClient';
import { USERS_API_BASE_URL, EPUBLISHER_API } from '../constants';
import {
  CreateContactGroupRequest,
  CreateContactGroupResponse,
} from '../types/ContactGroupCreation';
import { group } from 'console';

/**
 * Get contact group IDs with pagination
 * @param authToken - Authentication token
 * @param offset - Pagination offset (default: 0)
 * @returns Promise with group list
 * @throws ApiError if request fails
 */
export const getContactGroupIds = async (
  authToken: string,
  offset = 0
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/group/groupList/${offset}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Create new contact group
 * @param postData - Contact group creation data
 * @param authToken - Authentication token
 * @returns Promise with created contact group
 * @throws ApiError if creation fails
 */
export const createContactGroup = async (
  postData: CreateContactGroupRequest,
  authToken: string
): Promise<CreateContactGroupResponse> => {
  try {
    const response = await apiClient.post<CreateContactGroupResponse>(
      `${USERS_API_BASE_URL}/group`,
      postData,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update existing contact group
 * @param postData - Updated group data
 * @param groupId - Group ID to update
 * @param authToken - Authentication token
 * @returns Promise with update response
 * @throws ApiError if update fails
 */
export const updateContactGroup = async (
  postData: any,
  groupId: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.put(
      `${USERS_API_BASE_URL}/group/${groupId}`,
      postData,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get selected recipient data for a group
 * @param groupId - Group ID
 * @param authToken - Authentication token
 * @returns Promise with recipient data
 * @throws ApiError if request fails
 */
export const getSelectedRecipientData = async (
  groupId: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/group/${groupId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const getSelectUserGroupForDM = async (
  groupId: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/group/direct-message/details/${groupId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Get users associated with a contact group
 * @param groupId - Group ID
 * @param authToken - Authentication token
 * @returns Promise with group users/recipients
 * @throws ApiError if request fails
 */
export const getSelectUsersForGroup = async (
  groupId: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/group/msg-recipients/${groupId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get true member count from Firestore for a group
 * @param groupId - Group UUID
 * @param authToken - Authentication token
 * @returns Promise with total member count
 */
export const getGroupMemberCount = async (
  groupId: string,
  authToken: string
): Promise<number> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/group/member-count/${groupId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data.total;
  } catch (error) {
    console.error('Error fetching group member count:', error);
    return 0;
  }
};

/**
 * Trigger a background Firestore membership re-sync for a group from MySQL usergrouprel
 * @param groupId - Group UUID
 * @param authToken - Authentication token
 */
export const syncFirestoreMembers = async (
  groupId: string,
  authToken: string
): Promise<void> => {
  try {
    await apiClient.post(
      `${USERS_API_BASE_URL}/group/sync-firestore-members/${groupId}`,
      {},
      { headers: { Authorization: authToken } }
    );
  } catch (error) {
    console.error('Error triggering Firestore member sync:', error);
  }
};

/**
 * Get users detail by group
 * @param groupId - Group ID
 * @param authToken - Authentication token
 * @returns Promise with detailed user data
 * @throws ApiError if request fails
 */
export const getUsersDetailByGroup = async (
  groupId: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/group/groupUsersDetail/${groupId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Deactivate notification thread/contact group
 * @param id - Group/thread ID
 * @param postData - Deactivation data
 * @param authToken - Authentication token
 * @returns Promise with deactivation response
 * @throws ApiError if deactivation fails
 */
export const deactivateNotificationThread = async (
  id: string,
  postData: any,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.patch(
      `${USERS_API_BASE_URL}/group/${id}`,
      postData,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Delete notification thread/contact group
 * @param id - Group/thread ID to delete
 * @param authToken - Authentication token
 * @returns Promise with deletion response
 * @throws ApiError if deletion fails
 */
export const deleteNotificationThread = async (
  id: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.delete(
      `${USERS_API_BASE_URL}/group/${id}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Preview selected recipients for contact group
 * @param postData - Recipient selection criteria
 * @param authToken - Authentication token
 * @returns Promise with recipient preview
 * @throws ApiError if request fails
 */
export const previewSelectedRecipients = async (
  postData: any,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${USERS_API_BASE_URL}/contact-groups/preview-recipients`,
      postData,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Fetch email-only subscribers from ePublisher
 * @param epubOrgId - ePublisher organization ID
 * @param authToken - Authentication token
 * @returns Promise with email subscribers or null if timeout/error
 * @throws ApiError if request fails
 */
export const fetchEmailOnlySubscribers = async (
  epubOrgId: string,
  authToken: string
): Promise<any> => {
  if (!epubOrgId) {
    console.error('[Group Service] EpubOrgId is required to fetch email subscribers');
    throw new Error('EpubOrgId is required');
  }

  try {
    const response = await apiClient.get(
      `${EPUBLISHER_API}/category/${epubOrgId}`,
      {
        headers: {
          Authorization: authToken,
          'x-tenant-id': epubOrgId,
        },
        timeout: 10000, // 10 second timeout for ePublisher API
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.code === 'ECONNABORTED') {
      console.warn('[Group Service] Request timeout for fetchEmailOnlySubscribers');
      return null;
    }
    throw handleApiError(error);
  }
};
